// Pure rules behind the referee score screen (native Wertung tab; mirrors
// apps/referee/src/screens/Score.tsx + IpscKeypad/NumericKeypad and
// lib/timer/countUp.ts). Kept free of React/storage so they are unit-tested.
import { HitFactorConfig, type IpscZone, type ScoringMode } from './types.js';

/**
 * DQ reason the hard-PAR auto-DQ writes. The server stores it as
 * `dq_reason`, so after a refetch the referee can still tell a PAR auto-DQ
 * (hart-offen keeps recording) from a manual DQ (UI collapses).
 */
export const PAR_AUTO_DQ_REASON = 'PAR überschritten (Auto-DQ, harter PAR)';

export type ParMode = 'soft' | 'hard' | 'hard_stop';

/** Default ring keypad when the schema defines no zones. */
export const DEFAULT_RING_VALUES: readonly number[] = [10, 9, 8, 7, 6, 5, 4, 3, 2, 1, 0];

/** Keypad values in the schema's zone order (PWA NumericKeypad `values`). */
export function ringKeypadValues(
  zones: ReadonlyArray<{ valueNumeric: number; ordering: number }>,
): number[] {
  if (zones.length === 0) return [...DEFAULT_RING_VALUES];
  const seen = new Set<number>();
  const out: number[] = [];
  for (const z of [...zones].sort((a, b) => a.ordering - b.ordering)) {
    if (seen.has(z.valueNumeric)) continue;
    seen.add(z.valueNumeric);
    out.push(z.valueNumeric);
  }
  return out;
}

export interface IpscPoints {
  points: Record<IpscZone, number>;
  power: 'major' | 'minor';
}

/**
 * Zone points of a hit-factor schema config (same knobs the server's
 * `scoreIpsc` uses); unknown / invalid config → IPSC minor defaults.
 */
export function ipscPoints(config: unknown): IpscPoints {
  const parsed = HitFactorConfig.safeParse(
    config && typeof config === 'object' ? { kind: 'hit_factor', ...config } : { kind: 'hit_factor' },
  );
  const cfg = parsed.success ? parsed.data : HitFactorConfig.parse({ kind: 'hit_factor' });
  const major = cfg.power === 'major';
  return {
    power: cfg.power,
    points: {
      A: cfg.alphaPoints,
      C: major ? cfg.charlieMajor : cfg.charlieMinor,
      D: major ? cfg.deltaMajor : cfg.deltaMinor,
      M: cfg.missPenalty,
      NS: cfg.noShootPenalty,
    },
  };
}

/** Raw round score as the server computes it: shots + penalties. */
export function roundPoints(
  kind: 'ring_value' | 'hit_factor',
  shots: ReadonlyArray<{ valueNumeric: number | null; zoneCode: string | null }>,
  penalties: ReadonlyArray<{ points: number }>,
  ipsc: IpscPoints | null,
): number {
  let raw = 0;
  for (const s of shots) {
    if (kind === 'hit_factor') {
      const z = s.zoneCode as IpscZone | null;
      if (z && ipsc && z in ipsc.points) raw += ipsc.points[z];
    } else {
      raw += s.valueNumeric ?? 0;
    }
  }
  for (const p of penalties) raw += p.points;
  return raw;
}

/** Hit factor preview = max(0, points) / seconds; null without a time. */
export function hitFactor(points: number, elapsedMs: number | null | undefined): number | null {
  if (elapsedMs == null || elapsedMs <= 0) return null;
  return Math.max(0, points) / (elapsedMs / 1000);
}

/**
 * Whether a round has a hit factor at all. The SCORING MODE decides, not the
 * schema kind: the engine (`scoreRingValue` / `scoreIpsc`) computes it in
 * speed mode for both kinds and never in precision mode. The schema kind
 * only picks the keypad + how points are summed.
 */
export function modeHasHitFactor(mode: ScoringMode): boolean {
  return mode === 'speed';
}

/** HF preview under the engine's rule: null in precision mode or without a time. */
export function roundHitFactor(
  mode: ScoringMode,
  points: number,
  elapsedMs: number | null | undefined,
): number | null {
  return modeHasHitFactor(mode) ? hitFactor(points, elapsedMs) : null;
}

/** Cumulative magazine boundaries, excluding the final magazine end. */
export function magazineBoundaries(magazines: ReadonlyArray<{ shots: number }> | null | undefined): number[] {
  const out: number[] = [];
  if (!magazines || magazines.length <= 1) return out;
  let acc = 0;
  for (let i = 0; i < magazines.length - 1; i += 1) {
    acc += magazines[i]!.shots;
    out.push(acc);
  }
  return out;
}

/** "2+5" for a split magazine breakdown; null for none / a single magazine. */
export function formatMagazines(magazines: ReadonlyArray<{ shots: number }> | null | undefined): string | null {
  if (!magazines || magazines.length <= 1) return null;
  return magazines.map((m) => m.shots).join('+');
}

/**
 * "Nachladen" cue: the recorded shots exactly fill a magazine and the round
 * goes on. Returns the emptied magazine's number (1-based) or null.
 */
export function reloadDueMagazine(
  magazines: ReadonlyArray<{ shots: number }> | null | undefined,
  recorded: number,
  active: boolean,
): number | null {
  if (!active) return null;
  const idx = magazineBoundaries(magazines).indexOf(recorded);
  return idx < 0 ? null : idx + 1;
}

/** The round's DQ came from the hard-PAR auto-DQ (stored reason, or fired here just now). */
export function isParAutoDq(dq: boolean, dqReason: string | null | undefined, firedLocally: boolean): boolean {
  return dq && (dqReason === PAR_AUTO_DQ_REASON || (firedLocally && dqReason == null));
}

/**
 * hart-offen: a PAR auto-DQ does not tear down recording — the timer keeps
 * capturing and the referee keeps scoring up to the shot count.
 */
export function keepRecordingThroughDq(input: {
  parAutoDq: boolean;
  locked: boolean;
  done: boolean;
  parMode: ParMode;
}): boolean {
  return input.parAutoDq && !input.locked && !input.done && input.parMode === 'hard';
}

/**
 * Time to push to the device as its cap (0.1 s units; 0 = none): only
 * hart-stopp pins it to PAR, everything else gets the recording bound.
 */
export function deviceTimeLimitDS(
  parMode: ParMode,
  parSeconds: number | null,
  maxRecordingSeconds: number | null,
): number {
  const s = parMode === 'hard_stop' ? parSeconds : maxRecordingSeconds;
  return s != null ? Math.round(s * 10) : 0;
}

/** PAR progress of a run: fraction (0..1, capped) and whether PAR is exceeded. */
export function parProgress(
  elapsedMs: number | null | undefined,
  parSeconds: number | null,
): { fraction: number; over: boolean } | null {
  if (parSeconds == null || parSeconds <= 0) return null;
  const ms = Math.max(0, elapsedMs ?? 0);
  const parMs = parSeconds * 1000;
  return { fraction: Math.min(1, ms / parMs), over: ms > parMs };
}

/**
 * Per-shot display times: the first shot's time from the start beep, then
 * the split to the previous shot. `null` where a time is unknown.
 */
export function shotSplits(times: ReadonlyArray<number | null | undefined>): Array<{
  value: number;
  isSplit: boolean;
} | null> {
  return times.map((t, i) => {
    if (t == null) return null;
    if (i === 0) return { value: t, isSplit: false };
    const prev = times[i - 1];
    return prev == null ? { value: t, isSplit: false } : { value: t - prev, isSplit: true };
  });
}

/** Scenario count-up "M:SS.ss" (1750 ms → "0:01.75"). */
export function formatCountUp(ms: number): string {
  const totalCs = Math.max(0, Math.floor(ms / 10));
  const cs = totalCs % 100;
  const totalSec = Math.floor(totalCs / 100);
  const sec = totalSec % 60;
  const min = Math.floor(totalSec / 60);
  return `${min}:${sec.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
}
